import { ArrowUpIcon } from '@synoptic/ui-kit/icons/react/arrow-up.js';
import { space } from '@synoptic/ui-kit/tokens.js';
import { useComposedRefs } from '@synoptic/ui-kit/utils/use-composed-refs.js';
import {
  RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Location } from 'react-router';
import {
  Components,
  ItemContent,
  Virtuoso,
  VirtuosoHandle,
  VirtuosoProps,
} from 'react-virtuoso';
import { useScrollParent } from '../scroll-parent-context';
import { FeedScrollerWithFloatingHeader } from './feed-scroller-with-floating-header';
import { useRestoreFeed } from './restore-feed/use-restore-feed';

// Virtuoso requires a non-negative `firstItemIndex`; start high so we can decrease.
// https://virtuoso.dev/react-virtuoso/api-reference/virtuoso/#firstitemindex-number
const INITIAL_FIRST_ITEM_INDEX = 100000;

type Prepend = (newValue: number | ((prev: number) => number)) => void;

export type onRestoreFeed = (args: {
  prepend: Prepend;
  setOutdated: (v: boolean) => void;
  fetchOptions: Partial<Request>;
}) => void;

export type OnResetFeed = () => void;

export type FeedHandle = VirtuosoHandle & {
  prepend: Prepend;
};

export type FeedProps<Data, Context> = Omit<
  VirtuosoProps<Data, Context>,
  'customScrollParent' | 'onReset'
> & {
  itemContent: ItemContent<Data, Context>;
  feedRef?: RefObject<FeedHandle | null>;
  gap?: number;

  getStorageKey?: (location: Location) => string;
  onRestore?: onRestoreFeed;
  onReset?: OnResetFeed;
  getBackToTopLabel?: (prependedItems: number) => string;
  compactFeed?: boolean;
};

const defaultBackToTopLabel = (n: number) => `${n} New items`;

export const Feed = <Data, Context>({
  feedRef,
  gap,
  itemContent,
  increaseViewportBy = 800,
  totalListHeightChanged,
  getBackToTopLabel = defaultBackToTopLabel,
  compactFeed,
  atTopStateChange,
  getStorageKey,
  onRestore,
  onReset,
  context,
  ...props
}: FeedProps<Data, Context>) => {
  const ref = useRef<VirtuosoHandle>(null);
  const { scrollParent } = useScrollParent();

  const [firstItemIndex, setFirstItemIndex] = useState(
    INITIAL_FIRST_ITEM_INDEX,
  );
  const [prependedItems, setPrependedItems] = useState(0);

  const prepend: Prepend = (v) => {
    setPrependedItems((prev) => {
      const newValue = typeof v === 'function' ? v(prev) : v;
      const delta = newValue - prev;

      if (delta > 0) {
        setFirstItemIndex((prevIndex) => prevIndex - delta);
      }

      return newValue;
    });
  };

  const {
    restoreStateFrom,
    style,
    virtuosoRef,
    totalListHeightChanged: totalListHeightChangedRestoreFeed,
  } = useRestoreFeed({
    getKey: getStorageKey,
    onRestore: ({ fetchOptions }) =>
      onRestore?.({ prepend, setOutdated, fetchOptions }),
  });

  const [outdated, setOutdated] = useState(false);

  const isWindowScroll = scrollParent === document.scrollingElement;

  useImperativeHandle(feedRef, () => ({
    prepend,
    ...ref.current!,
  }));

  const composedRef = useComposedRefs(ref, virtuosoRef);

  const itemContentWithGap: ItemContent<Data, Context> = gap
    ? (idx, data, context) => (
        <div style={idx > 0 ? { paddingTop: gap } : undefined}>
          {itemContent?.(idx, data, context)}
        </div>
      )
    : itemContent;

  const scrollToStart = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      align: 'start',
      offset: -space.s64,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const floatingHeader = useMemo(() => {
    if (outdated) {
      return {
        title: 'The feed is outdated. Click to refresh',
        icon: <ArrowUpIcon />,
        onClick: () => {
          scrollToStart();
          setOutdated(false);
          onReset?.();
        },
        compactFeed,
      };
    }

    if (prependedItems <= 0) return null;

    return {
      title: getBackToTopLabel(prependedItems),
      icon: <ArrowUpIcon />,
      onClick: scrollToStart,
      compactFeed,
    };
  }, [
    getBackToTopLabel,
    onReset,
    outdated,
    prependedItems,
    scrollToStart,
    compactFeed,
  ]);

  const feedContext = useMemo(
    () =>
      floatingHeader
        ? {
            ...context,
            floatingHeader,
          }
        : context,
    [context, floatingHeader],
  );

  return (
    <>
      <Virtuoso<Data, Context>
        ref={composedRef}
        context={feedContext as Context}
        {...props}
        components={{
          Scroller: FeedScrollerWithFloatingHeader as Components<
            Data,
            Context
          >['Scroller'],
          ...props.components,
        }}
        initialItemCount={restoreStateFrom ? 0 : props.initialItemCount}
        style={{ ...style, ...props.style }}
        restoreStateFrom={restoreStateFrom}
        atTopStateChange={(atTop) => {
          if (atTop) {
            setPrependedItems(0);
          }

          atTopStateChange?.(atTop);
        }}
        useWindowScroll={isWindowScroll}
        itemContent={itemContentWithGap}
        firstItemIndex={firstItemIndex}
        totalListHeightChanged={(height) => {
          totalListHeightChanged?.(height);
          totalListHeightChangedRestoreFeed?.();
        }}
        customScrollParent={(!isWindowScroll && scrollParent) || undefined}
        increaseViewportBy={increaseViewportBy}
      />
    </>
  );
};
